<template>
  <Layout>
    <section class="marketplace--returns scroll-x-mobile">
      <header class="hide sm:flex sm:items-start sm:justify-between sm:flex-wrap sm:gap-md">
        <h2 class="marketplace-heading mb-md sm:mb-0">{{ 'Returns' | t }}</h2>
        <SloField class="m-0">
          <SloInput
            v-if="!loading && pagination.totalResults || queryParams.searchTerm"
            v-model="queryParams.searchTerm"
            type="text"
            :placeholder="'Search returns' | t"
          >
            <template v-slot:start>
              <SloIcon name="MagnifyingGlass" />
            </template>
          </SloInput>
        </SloField>
      </header>
      <main>
        <section class="marketplace--returns block sm:hide">
          <header class="mb-sm">
            <h2 class="marketplace-heading mb-md md:mb-0">{{ 'Returns' | t }}</h2>
            <SloField class="m-0">
              <SloInput
                v-if="!loading && pagination.totalResults || queryParams.searchTerm"
                v-model="queryParams.searchTerm"
                :placeholder="'Search returns' | t"
                type="text"
              >
                <template v-slot:start>
                  <SloIcon name="MagnifyingGlass" />
                </template>
              </SloInput>
            </SloField>
          </header>
        </section>
        <EmptyCartTeaser v-if="!loading && !isSearchingOrFiltering && pagination.totalResults === 0">
          <template v-slot:header>
            <h2 class="text-xl fw-strong">{{ 'You have no returns' | t }}</h2>
            <p class="m-0">{{ 'Shop the following categories for Sloan parts' | t }}</p>
          </template>
        </EmptyCartTeaser>
        <div v-else>
          <SloLoader :loading="loading" size="xl">
            <div class="flex items-center justify-start gap-md mt-md md:mt-0">
              <p v-if="returnRequests.length" class="m-0">
                {{ 'Showing' | t }} {{ pagination?.totalResults }} {{ pagination?.totalResults > 1 ? 'results' : 'result' | t }}
              </p>
              <SloSelect v-model="queryParams.dateRange" class="marketplace--returns-date-range">
                <option value="all" selected>{{ 'All Transactions' | t }}</option>
                <option value="3">{{ 'Last 3 months' | t }}</option>
                <option value="6">{{ 'Last 6 months' | t }}</option>
                <option value="12">{{ 'Last 12 months' | t }}</option>
              </SloSelect>
            </div>
            <SloLoader :loading="searchAndFilterLoading" size="xl">
              <div
                v-if="!hasSearchAndFilterResults && isSearchingOrFiltering"
                 class="marketplace--returns-no-results"
              >
                <p>{{ 'No returns found' | t }}</p>
              </div>
              <div v-for="returnRequest in returnRequests" :key="returnRequest?.rma" class="mt-lg">
                <MarketplaceTable v-if="returnRequest?.returnEntries">
                  <template v-slot:header>
                    <MarketplaceTableHeader>
                      <template v-slot:titles>
                        <div class="flex items-start justify-between md:flex-col md:justify-start">
                          <h4 class="text-base fw-strong">{{ 'Return Order Number' | t }}</h4>
                          <span class="text-base-c-gray-700">{{ returnRequest?.rma | formatOrderNumber }}</span>
                        </div>
                        <div class="flex items-start justify-between md:flex-col md:justify-start">
                          <h4 class="text-base fw-strong">{{ 'Date Placed' | t }}</h4>
                          <span class="text-base-c-gray-700">{{ returnRequest?.creationDate }}</span>
                        </div>
                        <div class="flex items-start justify-between md:flex-col md:justify-start">
                          <h4 class="text-base fw-strong">{{ 'Items' | t }}</h4>
                          <span class="text-base-c-gray-700">{{ returnRequest?.totalItems }}</span>
                        </div>
                      </template>
                      <template v-slot:buttons>
                        <SloButton
                          :to="{
                            name: 'marketplace-account-return',
                            params: {code: returnRequest?.rma},
                            query: currentRouteFullPath ? {back: currentRouteFullPath} : {}
                          }"
                          fill="outline"
                        >
                          {{ 'View Details' | t }}
                        </SloButton>
                      </template>
                    </MarketplaceTableHeader>
                  </template>
                  <template v-slot:body>
                    <MarketplaceRow
                      v-for="(returnEntry, index) in returnRequest?.returnEntries.slice(0, returnRequestDisplayLength[returnRequest?.code] || 3)"
                      :key="`return-entry-${index}`"
                      :title="returnEntry?.orderEntry?.product?.number"
                      :description="returnEntry?.orderEntry?.product?.title"
                      :sku="returnEntry?.orderEntry?.product?.code"
                      :product-type="returnEntry?.orderEntry?.product?.type"
                      :price="returnEntry?.orderEntry?.basePrice?.formattedValue"
                      :uom="returnEntry?.orderEntry?.product?.uom"
                      :quantity="returnEntry?.expectedQuantity"
                      :total="returnEntry?.refundAmount?.formattedValue"
                    >
                      <template v-slot:image>
                        <SloProductImage :source="returnEntry?.orderEntry?.product?.images | getProductThumbnail" />
                      </template>
                    </MarketplaceRow>
                  </template>
                  <template v-slot:footer>
                    <MarketplaceTableFooter v-if="returnRequest?.returnEntries.length > 3">
                      <SloButton
                        v-if="returnRequestDisplayLength[returnRequest?.code]"
                        @click="removeReturnEntriesDisplayLengthItem(returnRequest)"
                        fill="clear"
                        class="flex items-center gap-xs p-0 bg-transparent c-blue-400 hover:c-blue-700"
                      >
                        <SloIcon name="Minus" />
                        {{ 'Collapse' | t }}
                      </SloButton>
                      <div v-else class="flex items-baseline justify-start gap-sm">
                        <SloButton
                          @click="addReturnEntriesDisplayLengthItem(returnRequest)"
                          fill="clear"
                          class="flex items-center gap-xs p-0 bg-transparent c-blue-400 hover:c-blue-700"
                        >
                          <SloIcon name="Plus" />
                          {{ returnRequest?.returnEntries.length - 3 }}
                          {{ 'more items' | t }}
                        </SloButton>
                        <div class="marketplace--returns-more-items">
                          <SloProductImage
                            v-for="(orderEntry, moreOrderEntryKey) in returnRequest?.returnEntries.slice(3, returnRequest?.returnEntries.length)"
                            :key="`more-order-entry-${moreOrderEntryKey}`"
                            :source="orderEntry?.orderEntry?.product?.images | getProductThumbnail"
                          />
                        </div>
                      </div>
                    </MarketplaceTableFooter>
                    <div class="customer-return flex flex-col items-start gap-sm">
                        <div class="flex items-start gap-sm">
                          <p class="text-base fw-strong mb-0">{{ 'Reason' | t }}</p>
                          <p v-if="returnRequest?.refundReason" class="mb-0 word-break-word">
                            {{ returnRequest?.refundReason }}
                          </p>
                        </div>
                        <div v-if="returnRequest?.notes" class="flex items-start gap-sm">
                          <p class="text-base fw-strong mb-0">{{ 'Notes' | t }}</p>
                          <p class="mb-0 word-break-word">
                            {{ returnRequest?.notes }}
                          </p>
                        </div>
                      </div>
                  </template>
                </MarketplaceTable>
              </div>
              <SloPagination
                v-if="pagination.totalResults > pagination.pageSize"
                :total-pages="pagination.totalPages"
                :current-page="parseInt(queryParams.currentPage)"
                :max-visible-pages="10"
                @pageChanged="handlePageChange"
              />
            </SloLoader>
          </SloLoader>
        </div>
      </main>
    </section>
  </Layout>
</template>

<script>
import {debounce} from 'vue-debounce';
import t from '../../main/i18n';
import formatDate from '../../../lib/filters/formatDate';
import {marketplaceApi} from '../apis/marketplace';
import {getProductThumbnail, formatOrderNumber, handleApiErrors} from '../../main/utils';
import Layout from '../components/Layout.vue';
import EmptyCartTeaser from '../components/EmptyCartTeaser.vue';
import SloLink from '../../../lib/components/SloLink.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloProductImage from '../../../lib/components/SloProductImage.vue';
import SloPagination from '../../../lib/components/SloPagination.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloSelect from '../../../lib/components/SloSelect.vue';
import MarketplaceTable from '../components/MarketplaceTable.vue';
import MarketplaceTableHeader from '../components/MarketplaceTableHeader.vue';
import MarketplaceRow from '../components/MarketplaceRow.vue';
import MarketplaceTableFooter from '../components/MarketplaceTableFooter.vue';

export default {
  components: {
    Layout,
    EmptyCartTeaser,
    SloLink,
    SloButton,
    SloProductImage,
    SloPagination,
    SloLoader,
    SloField,
    SloInput,
    SloLabel,
    SloIcon,
    SloSelect,
    MarketplaceTable,
    MarketplaceTableHeader,
    MarketplaceRow,
    MarketplaceTableFooter
  },
  filters: {
    t,
    formatDate,
    getProductThumbnail,
    formatOrderNumber
  },
  data() {
    return {
      loading: true,
      searchAndFilterLoading: false,
      isFirstPageLoad: true,
      returnRequests: [],
      returnRequestDisplayLength: [],
      currentRouteFullPath: '',
      queryParams: {
        dateRange: 'all',
        currentPage: 1,
        searchTerm: ''
      },
      pagination: {
        pageSize: 3,
        totalPages: 0,
        totalResults: 0
      },
      startDate: '',
      endDate: ''
    };
  },
  created() {
    this.queryParams = {...this.queryParams, ...this.$route.query};
    this.currentRouteFullPath = this.$router.currentRoute.fullPath;
  },
  computed: {
    isSearchingOrFiltering() {
      return this.queryParams.searchTerm || this.queryParams.dateRange !== 'all';
    },
    hasSearchAndFilterResults() {
      return this.isSearchingOrFiltering && this.pagination.totalResults > 0;
    }
  },
  watch: {
    $route() {
      this.currentRouteFullPath = this.$router.currentRoute.fullPath;
    },
    queryParams: {
      handler() {
        if (
          this.queryParams.searchTerm === ''
          || this.queryParams.searchTerm.length > 2
          || this.returnRequests.length === 0
        ) {
          this.searchAndFilterLoading = true;
          this.setDateRangeValue();
          (debounce(() => this.updateQuery(), '300ms'))();
        }
      },
      deep: true
    },
    'queryParams.dateRange'(newValue, oldValue) {
      if (newValue !== oldValue && !this.$route?.query?.currentPage) {
        this.queryParams.currentPage = 1;
      }
    },
    'queryParams.searchTerm'(newValue, oldValue) {
      if (newValue !== oldValue && !this.$route?.query?.currentPage) {
        this.queryParams.currentPage = 1;
      }
    }
  },
  methods: {
    async updateQuery() {
      const query = Object.entries(this.queryParams).reduce((acc, [key, val]) => {
        if (!val) return acc;
        return {...acc, [key]: val};
      }, {});

      await this.getReturns();

      if (!this.isFirstPageLoad) {
        await this.$router.push({
          name: 'marketplace-account-returns',
          query: {...query}
        }).catch(() => {});
      }
      this.isFirstPageLoad = false;
    },
    setDateRangeValue() {
      this.loading = true;

      if (this.queryParams.dateRange === 'all') {
        this.startDate = '';
        this.endDate = '';
      } else {
        const dateInPast = new Date();
        dateInPast.setMonth(dateInPast.getMonth() - parseInt(this.queryParams.dateRange));
        this.startDate = formatDate(dateInPast, 'YYYY-MM-DD');
        this.endDate = formatDate(new Date(), 'YYYY-MM-DD');
      }
    },
    handlePageChange(page) {
      window.scrollTo(0, 0);
      this.queryParams.currentPage = parseInt(page);
    },
    async getReturns() {
      await marketplaceApi.getCurrentUserReturns({
        currentPage: this.queryParams.currentPage - 1,
        pageSize: this.pagination.pageSize,
        startDate: this.startDate,
        endDate: this.endDate,
        searchTerm: this.queryParams.searchTerm
      })
        .then(res => {
          this.loading = false;
          this.searchAndFilterLoading = false;
          this.returnRequests = res.data.returnRequests;
          this.pagination = {
            currentPage: res.data?.pagination?.currentPage + 1,
            pageSize: res.data?.pagination?.pageSize,
            totalPages: res.data?.pagination?.totalPages,
            totalResults: res.data?.pagination?.totalResults
          };
        })
        .catch(e => {
          this.loading = false;
          this.searchAndFilterLoading = false;
          handleApiErrors(e, this, 'Error retrieving returns');
        });
    },
    addReturnEntriesDisplayLengthItem(returns) {
      this.returnRequestDisplayLength[returns?.code] = returns?.returnEntries.length;
      this.$forceUpdate();
    },
    removeReturnEntriesDisplayLengthItem(returns) {
      delete this.returnRequestDisplayLength[returns?.code];
      this.$forceUpdate();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../scss/variables';
@import '../../../scss/system';

.marketplace--returns {
  :deep(.slo-input-wrapper) {
    label {
      margin: 0;
    }
  }

  &-no-results {
    > p {
      margin: 3.2rem 0;
      font-size: 2.4rem;
    }
  }

  &-date-range {
    min-width: 164px;
  }

  &-more-items {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.8rem;

    :deep(.slo-product-image) {
      img {
        width: 3.6rem;
        height: 3.6rem;
      }
    }
  }
}

.customer-return {
  padding: 1.6rem;
  border-top: 1px solid $color-gray-400;
  background-color: #fffbeb;
  border-radius: 0 0 1.2rem 1.2rem;
}

.word-break-word {
  word-break: break-word;
}
</style>
